import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Alert,
  Skeleton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Room as RoomIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

const ServiceTasksList = ({ bookingId, isPreview = false, quotationId = null }) => {
  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPanels, setExpandedPanels] = useState({});

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const endpoint = isPreview 
          ? `/api/quotations/${quotationId}/tasks`
          : `/api/bookings/${bookingId}/tasks`;
        
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch task list');
        }

        const data = await response.json();
        setTaskData(data);
        
        // Auto-expand first room panel
        if (data.tasks && Object.keys(data.tasks).length > 0) {
          const firstRoom = Object.keys(data.tasks)[0];
          setExpandedPanels({ [firstRoom]: true });
        }
      } catch (err) {
        console.error('Error fetching tasks:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (bookingId || quotationId) {
      fetchTasks();
    }
  }, [bookingId, quotationId, isPreview]);

  const handlePanelChange = (panelId) => (event, isExpanded) => {
    setExpandedPanels(prev => ({
      ...prev,
      [panelId]: isExpanded
    }));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      'sanitation': '🧽',
      'surfaces': '✨',
      'floors': '🧹',
      'appliances': '🔧',
      'windows': '🪟',
      'dusting': '🪶',
      'organization': '📦',
      'maintenance': '🔨',
      'upholstery': '🛋️',
      'detail': '🔍',
      'deep_cleaning': '🧽',
      'plumbing': '🚿'
    };
    return iconMap[category] || '📝';
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Skeleton width="40%" />
          </Typography>
          <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={200} />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Error loading service tasks: {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!taskData) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            No task data available
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const { tasks, summary, propertyInfo } = taskData;

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h3">
            Service Task List {isPreview && '(Preview)'}
          </Typography>
        </Box>

        {/* Summary Information */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center" p={2} bgcolor="primary.light" borderRadius={1}>
              <Typography variant="h4" color="primary.contrastText" fontWeight="bold">
                {summary.totalTasks}
              </Typography>
              <Typography variant="body2" color="primary.contrastText">
                Total Tasks
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center" p={2} bgcolor="secondary.light" borderRadius={1}>
              <Typography variant="h4" color="secondary.contrastText" fontWeight="bold">
                {formatDuration(summary.totalEstimatedDuration)}
              </Typography>
              <Typography variant="body2" color="secondary.contrastText">
                Est. Duration
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center" p={2} bgcolor="success.light" borderRadius={1}>
              <Typography variant="h4" color="success.contrastText" fontWeight="bold">
                {propertyInfo.bedrooms}
              </Typography>
              <Typography variant="body2" color="success.contrastText">
                Bedrooms
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box textAlign="center" p={2} bgcolor="warning.light" borderRadius={1}>
              <Typography variant="h4" color="warning.contrastText" fontWeight="bold">
                {propertyInfo.bathrooms}
              </Typography>
              <Typography variant="body2" color="warning.contrastText">
                Bathrooms
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Property Information */}
        <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Property Details
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            <Chip icon={<RoomIcon />} label={`${propertyInfo.type}`} size="small" />
            <Chip icon={<ScheduleIcon />} label={`${propertyInfo.squareFootage} sq ft`} size="small" />
            <Chip label={`${propertyInfo.sqftMultiplier}x duration`} size="small" variant="outlined" />
          </Box>
        </Box>

        {/* Task List by Room */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Tasks by Room
          </Typography>
          
          {Object.entries(tasks).map(([roomName, roomData]) => (
            <Accordion
              key={roomName}
              expanded={expandedPanels[roomName] || false}
              onChange={handlePanelChange(roomName)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  bgcolor: 'grey.50',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
              >
                <Box display="flex" alignItems="center" width="100%">
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {roomData.roomName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {roomData.taskCount} tasks • {formatDuration(roomData.totalDuration)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(roomData.totalDuration / summary.totalEstimatedDuration) * 100}
                    sx={{ width: 100, mr: 2 }}
                  />
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                <List disablePadding>
                  {roomData.tasks.map((task, index) => (
                    <ListItem key={task.id} divider={index < roomData.tasks.length - 1}>
                      <ListItemIcon>
                        <Typography variant="h6">
                          {getCategoryIcon(task.category)}
                        </Typography>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body1">
                              {task.name}
                            </Typography>
                            {task.isDeepCleaning && (
                              <Chip label="Deep Clean" size="small" color="primary" variant="outlined" />
                            )}
                            {task.isExtraService && (
                              <Chip label="Extra Service" size="small" color="secondary" variant="outlined" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <Chip
                              label={task.priority}
                              size="small"
                              color={getPriorityColor(task.priority)}
                              variant="outlined"
                            />
                            <Chip
                              label={task.category}
                              size="small"
                              variant="outlined"
                            />
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <ScheduleIcon fontSize="small" />
                              <Typography variant="caption">
                                {formatDuration(task.estimatedDuration)}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                      <CheckCircleIcon
                        color={task.status === 'completed' ? 'success' : 'disabled'}
                        fontSize="small"
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Service Types */}
        {summary.serviceTypes && summary.serviceTypes.length > 0 && (
          <Box mt={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Included Services
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {summary.serviceTypes.map((service, index) => (
                <Chip key={index} label={service} size="small" color="primary" />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ServiceTasksList;